import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Calendar, ArrowRight, Clock, Tag, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LatestUpdatesPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch(`${API_URL}/api/blog`);
        const data = await response.json();
        setPosts(data);
      } catch (error) {
        console.error('Error fetching posts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Latest Updates - ToolsForge Blog",
    "description": "Stay updated with the latest crypto calculator guides, tool tutorials, and investment insights. Updated for 2026.",
    "url": "https://toolsforgeapp.com/latest-updates",
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": posts.length,
      "itemListElement": posts.slice(0, 20).map((post, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "url": `https://toolsforgeapp.com/blog/${post.slug}`
      }))
    }
  };

  return (
    <>
      <SEOHead
        title="Latest Updates (2026) - Crypto Calculators & Tool Guides | ToolsForge"
        description="Discover the latest crypto calculator guides, investment tutorials, and tool updates. 49+ articles on Bitcoin, Ethereum, altcoin profits, taxes & ROI. Updated March 2026."
        keywords="crypto calculator updates, bitcoin profit guide 2026, ethereum calculator, crypto tax guide, latest crypto tools"
        canonical="https://toolsforgeapp.com/latest-updates"
      />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />

      <div className="min-h-screen py-20 px-6" data-testid="latest-updates-page">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary transition-colors">Home</Link>
            <span>/</span>
            <span className="text-foreground">Latest Updates</span>
          </nav>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <Badge variant="outline" className="mb-4 px-4 py-1.5 border-green-500/30 text-green-400">
              <Clock className="w-3 h-3 mr-2" />
              Updated March 2026
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 font-['Plus_Jakarta_Sans']">
              Latest Updates & Guides
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Stay current with our newest crypto calculator guides, investment tutorials, and tool updates. 
              All content freshly updated for 2026.
            </p>
          </motion.div>

          {/* Quick Links */}
          <div className="grid md:grid-cols-3 gap-4 mb-12">
            <Link to="/crypto-calculators" className="p-4 rounded-xl border border-border bg-card hover:border-primary/50 transition-all group">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <h3 className="font-semibold group-hover:text-primary transition-colors">Crypto Calculators</h3>
                  <p className="text-sm text-muted-foreground">15 free tools</p>
                </div>
              </div>
            </Link>
            <Link to="/blog" className="p-4 rounded-xl border border-border bg-card hover:border-primary/50 transition-all group">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Tag className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold group-hover:text-primary transition-colors">All Blog Posts</h3>
                  <p className="text-sm text-muted-foreground">{posts.length} articles</p>
                </div>
              </div>
            </Link>
            <Link to="/top-tools" className="p-4 rounded-xl border border-border bg-card hover:border-primary/50 transition-all group">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h3 className="font-semibold group-hover:text-primary transition-colors">Top Tools</h3>
                  <p className="text-sm text-muted-foreground">Most popular</p>
                </div>
              </div>
            </Link>
          </div>

          {/* All Posts List */}
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold mb-6 font-['Plus_Jakarta_Sans']">
              All Recent Articles ({posts.length})
            </h2>
            
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading articles...</div>
            ) : (
              <div className="grid gap-4">
                {posts.map((post, index) => (
                  <motion.article
                    key={post.slug}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.02 }}
                    className="p-4 rounded-xl border border-border bg-card hover:border-primary/50 transition-all"
                  >
                    <Link to={`/blog/${post.slug}`} className="block group">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="w-3 h-3 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">
                              {new Date(post.created_at).toLocaleDateString('en-US', { 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric' 
                              })}
                            </span>
                            <Badge variant="outline" className="text-xs px-2 py-0 border-green-500/30 text-green-400">
                              Updated 2026
                            </Badge>
                          </div>
                          <h3 className="font-semibold text-lg group-hover:text-primary transition-colors mb-1">
                            {post.title}
                          </h3>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {post.excerpt}
                          </p>
                          {post.tags && post.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {post.tags.slice(0, 4).map(tag => (
                                <Badge key={tag} variant="secondary" className="text-xs px-2 py-0">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 mt-1" />
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </div>
            )}
          </div>

          {/* CTA Section */}
          <div className="mt-16 p-8 rounded-2xl border border-border bg-gradient-to-b from-primary/5 to-accent/5 text-center">
            <h2 className="text-2xl font-semibold mb-4 font-['Plus_Jakarta_Sans']">
              Start Calculating Your Crypto Profits
            </h2>
            <p className="text-muted-foreground mb-6">
              Use our free calculators to track Bitcoin, Ethereum, and altcoin investments.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button asChild>
                <Link to="/crypto-calculators">
                  Crypto Calculators <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/all-tools">
                  All 182 Tools
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LatestUpdatesPage;
